import { defineStore } from 'pinia';
import {
  ItemLaneTransaction,
  IItemsTableWorkItem,
  ITimeSpentWorkItem,
} from '@/models/data.models';
import { CubeDataModule, DataModule } from '@/modules/data.module';
import { useOrganizationStore } from '@/store/organizationStore';
import { StoredQueryOptions } from '@/models/item.models';
import { DateTimeRange, DateTimeRangeOption } from '@/models/common.models';
import dayjs from '@/utils/dayjs';
import Notify from '@/utils/app-notification';
import { DSCubeQuery } from '@/models/cube.models';
import { Board } from '@/api';
import { CycleBoard } from '@/types/board-types';

export interface DataStateInterface {
  itemLaneTransactions: Array<ItemLaneTransaction>;
  loadingItemLaneTransactions: boolean;
  loadingMoreItemLaneTransactions: boolean;
  itemLaneTransactionsOffset: number | null;
  cubeToken: string | undefined;
  selectedDateRange: Omit<DateTimeRangeOption, 'displayRange'>;
}

function isTokenExpired(token: string) {
  const expiry = JSON.parse(atob(token.split('.')[1])).exp;
  return Math.floor(new Date().getTime() / 1000) >= expiry;
}

export const useDataStore = defineStore('data', {
  /****
   STATE
   *****/
  state: (): DataStateInterface => {
    return {
      itemLaneTransactions: [],
      loadingItemLaneTransactions: false,
      loadingMoreItemLaneTransactions: false,
      itemLaneTransactionsOffset: null,
      cubeToken: undefined,
      selectedDateRange: {
        id: 'last-14-days',
        label: 'Last 14 days',
        value: {
          from: dayjs().subtract(13, 'day').startOf('day').toISOString(),
          to: dayjs().endOf('day').toISOString(),
        },
      },
    };
  },

  /****
   GETTERS
   ****/
  getters: {},

  /****
   ACTIONS
   *****/
  actions: {
    clearItemLaneTransactions() {
      this.itemLaneTransactions = [];
      this.itemLaneTransactionsOffset = null;
      this.loadingItemLaneTransactions = false;
      this.loadingMoreItemLaneTransactions = false;
    },
    async getCurrentProgressData(filters?: StoredQueryOptions) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      const token = await this.getCubeAuth(organizationId);
      const cubeDataModule = new CubeDataModule(organizationId, token);
      return cubeDataModule.getCurrentProgressData(filters);
    },

    async getTrendingCompletedData(
      dateRange: DateTimeRange,
      dateGranularity: 'day' | 'week' | 'month',
      filters?: StoredQueryOptions
    ) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      const token = await this.getCubeAuth(organizationId);
      const cubeDataModule = new CubeDataModule(organizationId, token);
      return cubeDataModule.getTrendingCompletedData(
        dateRange,
        dateGranularity,
        filters
      );
    },

    async getTrendingTimeEstimatedData(
      dateRange: DateTimeRange,
      dateGranularity: 'day' | 'week' | 'month',
      filters?: StoredQueryOptions
    ) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      const token = await this.getCubeAuth(organizationId);
      const cubeDataModule = new CubeDataModule(organizationId, token);
      return cubeDataModule.getTrendingTimeEstimatedData(
        dateRange,
        dateGranularity,
        filters
      );
    },

    async getTrendingPointEstimatedData(
      dateRange: DateTimeRange,
      dateGranularity: 'day' | 'week' | 'month',
      filters?: StoredQueryOptions
    ) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      const token = await this.getCubeAuth(organizationId);
      const cubeDataModule = new CubeDataModule(organizationId, token);
      return cubeDataModule.getTrendingPointEstimatedData(
        dateRange,
        dateGranularity,
        filters
      );
    },

    async getTrendingAssignedData(
      dateRange: DateTimeRange,
      dateGranularity: 'day' | 'week' | 'month',
      filters?: StoredQueryOptions
    ) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      const token = await this.getCubeAuth(organizationId);
      const cubeDataModule = new CubeDataModule(organizationId, token);
      return cubeDataModule.getTrendingAssignedData(
        dateRange,
        dateGranularity,
        filters
      );
    },

    async getTrendingPlannedData(
      dateRange: DateTimeRange,
      dateGranularity: 'day' | 'week' | 'month',
      filters?: StoredQueryOptions
    ) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      const token = await this.getCubeAuth(organizationId);
      const cubeDataModule = new CubeDataModule(organizationId, token);
      return cubeDataModule.getTrendingPlannedData(
        dateRange,
        dateGranularity,
        filters
      );
    },

    async getTrendingLaneData(
      dateRange: DateTimeRange,
      dateGranularity: 'day' | 'week' | 'month',
      filters?: StoredQueryOptions
    ) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      const token = await this.getCubeAuth(organizationId);
      const cubeDataModule = new CubeDataModule(organizationId, token);
      return cubeDataModule.getTrendingLaneData(
        dateRange,
        dateGranularity,
        filters
      );
    },

    async getThroughputData(
      dateRange: DateTimeRange,
      dateGranularity: 'day' | 'week' | 'month',
      filters?: StoredQueryOptions
    ) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      const token = await this.getCubeAuth(organizationId);
      const cubeDataModule = new CubeDataModule(organizationId, token);
      return cubeDataModule.getThroughputData(
        dateRange,
        dateGranularity,
        filters
      );
    },

    async getCumulativeFlowData(
      dateRange: DateTimeRange,
      dateGranularity: 'day' | 'week' | 'month',
      filters?: StoredQueryOptions
    ) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      const token = await this.getCubeAuth(organizationId);
      const cubeDataModule = new CubeDataModule(organizationId, token);
      return cubeDataModule.getCumulativeFlowData(
        dateRange,
        dateGranularity,
        filters
      );
    },

    async getUserCompletedTimeItemTimeSpentTotalData(
      assigneeUsername: string,
      dateRange: DateTimeRange,
      filters?: StoredQueryOptions
    ) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;

      if (!organizationId) return;
      const token = await this.getCubeAuth(organizationId);
      const cubeDataModule = new CubeDataModule(organizationId, token);

      return cubeDataModule.getUserCompletedTimeItemTimeSpentTotalData(
        assigneeUsername,
        dateRange,
        filters
      );
    },

    async getUserCompletedTimeSubItemTimeSpentTotalData(
      assigneeUsername: string,
      dateRange: DateTimeRange,
      filters?: StoredQueryOptions
    ) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;

      if (!organizationId) return;
      const token = await this.getCubeAuth(organizationId);
      const cubeDataModule = new CubeDataModule(organizationId, token);

      return cubeDataModule.getUserCompletedTimeSubItemTimeSpentTotalData(
        assigneeUsername,
        dateRange,
        filters
      );
    },

    async getAllUsersTimeEntriesTotalData(
      dateRange: DateTimeRange,
      filters?: StoredQueryOptions
    ) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;

      if (!organizationId) return;
      const token = await this.getCubeAuth(organizationId);
      const cubeDataModule = new CubeDataModule(organizationId, token);

      return cubeDataModule.getAllUsersTimeEntriesTotalData(dateRange, filters);
    },

    async getAllUsersSubItemTimeSpentTotalData(
      dateRange: DateTimeRange,
      filters?: StoredQueryOptions
    ) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;

      if (!organizationId) return;
      const token = await this.getCubeAuth(organizationId);
      const cubeDataModule = new CubeDataModule(organizationId, token);

      return cubeDataModule.getAllUsersSubItemTimeSpentTotalData(
        dateRange,
        filters
      );
    },

    async getItemLaneTransactionData(
      dateRange?: DateTimeRange,
      filters?: StoredQueryOptions,
      clear?: boolean,
      selectedWorkTypeIds?: string[]
    ) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      const token = await this.getCubeAuth(organizationId);
      const cubeDataModule = new CubeDataModule(organizationId, token);

      if (clear) {
        this.itemLaneTransactionsOffset = null;
        this.loadingItemLaneTransactions = true;
      } else {
        this.loadingMoreItemLaneTransactions = true;
      }

      const responseLimit = 200;

      try {
        const response = await cubeDataModule.getItemTransactionData(
          dateRange,
          {
            ...(filters ?? {}),
            workTypeId: selectedWorkTypeIds,
          },
          {
            limit: responseLimit,
            offset: this.itemLaneTransactionsOffset || 0,
          }
        );

        if (response?.length === responseLimit) {
          this.itemLaneTransactionsOffset = this.itemLaneTransactionsOffset
            ? this.itemLaneTransactionsOffset + responseLimit
            : responseLimit;
        } else {
          this.itemLaneTransactionsOffset = null;
        }
        this.itemLaneTransactions = clear
          ? (response as ItemLaneTransaction[])
          : [
              ...this.itemLaneTransactions,
              ...(response as ItemLaneTransaction[]),
            ];
      } catch (error) {
        console.error(error);
        Notify({
          type: 'error',
          message:
            'There was a problem loading the transactions. If this problem persists, please contact support.',
        });
      } finally {
        this.loadingItemLaneTransactions = false;
        this.loadingMoreItemLaneTransactions = false;
      }
    },

    async getItemsData(
      {
        query,
        rowMapper,
      }: {
        query: DSCubeQuery;
        rowMapper: (row: Record<string, string>) => IItemsTableWorkItem;
      },
      paginationOptions: { limit: number; offset: number }
    ): Promise<{
      cursor: number | null;
      items: IItemsTableWorkItem[];
    } | null> {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) throw new Error('No organization selected');
      const token = await this.getCubeAuth(organizationId);
      const cubeDataModule = new CubeDataModule(organizationId, token);

      const limit = paginationOptions.limit;
      const offset = paginationOptions.offset;

      try {
        const response = await cubeDataModule.getItemsData(query, {
          limit,
          offset,
        });
        if (!response) {
          return {
            cursor: null,
            items: [],
          };
        }
        const cursor = response.length === limit ? offset + limit : null;

        const items: IItemsTableWorkItem[] = response.map(rowMapper);
        return {
          cursor,
          items,
        };
      } catch (error) {
        console.error(error);
        Notify({
          type: 'error',
          message:
            'There was a problem loading the items. If this problem persists, please contact support.',
        });
      }
      return null;
    },

    async getItemsWithSubItemsData(
      {
        query,
        rowMapper,
      }: {
        query: DSCubeQuery;
        rowMapper: (row: Record<string, string>) => ITimeSpentWorkItem;
      },
      paginationOptions: { limit: number; offset: number }
    ): Promise<{
      cursor: number | null;
      items: ITimeSpentWorkItem[];
    } | null> {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) throw new Error('No organization selected');
      const token = await this.getCubeAuth(organizationId);
      const cubeDataModule = new CubeDataModule(organizationId, token);

      const limit = paginationOptions.limit;
      const offset = paginationOptions.offset;

      try {
        const response = await cubeDataModule.getItemsData(query, {
          limit,
          offset,
        });
        if (!response) {
          return {
            cursor: null,
            items: [],
          };
        }
        const cursor = response.length === limit ? offset + limit : null;

        const items: ITimeSpentWorkItem[] = response.map(rowMapper);
        return {
          cursor,
          items,
        };
      } catch (error) {
        console.error(error);
        Notify({
          type: 'error',
          message:
            'There was a problem loading the items. If this problem persists, please contact support.',
        });
      }
      return null;
    },

    async getCubeAuth(organizationId: string) {
      const dataModule = new DataModule(organizationId);
      if (this.cubeToken) {
        if (!isTokenExpired(this.cubeToken)) return this.cubeToken;
      }
      this.cubeToken = await dataModule.getCubeAuth();
      return this.cubeToken;
    },

    async getChurnCycleData(
      cycles: CycleBoard[],
      filters?: StoredQueryOptions
    ) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      const token = await this.getCubeAuth(organizationId);
      const cubeDataModule = new CubeDataModule(organizationId, token);
      return cubeDataModule.getChurnCycleData(cycles, filters);
    },

    async getChurnPlannedUnplannedData(
      dateRange: DateTimeRange,
      dateGranularity: 'day' | 'week' | 'month',
      filters?: StoredQueryOptions
    ) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      const token = await this.getCubeAuth(organizationId);
      const cubeDataModule = new CubeDataModule(organizationId, token);
      return cubeDataModule.getChurnPlannedUnplannedData(
        dateRange,
        dateGranularity,
        filters
      );
    },

    async getChurnCreatedDeletedData(
      dateRange: DateTimeRange,
      dateGranularity: 'day' | 'week' | 'month',
      filters?: StoredQueryOptions
    ) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      const token = await this.getCubeAuth(organizationId);
      const cubeDataModule = new CubeDataModule(organizationId, token);
      return cubeDataModule.getChurnCreatedDeletedData(
        dateRange,
        dateGranularity,
        filters
      );
    },

    async getChurnAssignedUnassignedData(
      dateRange: DateTimeRange,
      dateGranularity: 'day' | 'week' | 'month',
      filters?: StoredQueryOptions
    ) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      const token = await this.getCubeAuth(organizationId);
      const cubeDataModule = new CubeDataModule(organizationId, token);
      return cubeDataModule.getChurnAssignedUnassignedData(
        dateRange,
        dateGranularity,
        filters
      );
    },

    async getCycleVelocityData(cycles: Board[], filters?: StoredQueryOptions) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      const token = await this.getCubeAuth(organizationId);
      const cubeDataModule = new CubeDataModule(organizationId, token);
      return cubeDataModule.getCycleVelocityData(cycles, filters);
    },

    async getLeadTimeData(
      dateRange: DateTimeRange,
      dateGranularity: 'day' | 'week' | 'month',
      filters?: StoredQueryOptions
    ) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      const token = await this.getCubeAuth(organizationId);
      const cubeDataModule = new CubeDataModule(organizationId, token);
      return cubeDataModule.getLeadTimeData(
        dateRange,
        dateGranularity,
        filters
      );
    },

    async getCycleTimeData(
      dateRange: DateTimeRange,
      dateGranularity: 'day' | 'week' | 'month',
      filters?: StoredQueryOptions
    ) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      const token = await this.getCubeAuth(organizationId);
      const cubeDataModule = new CubeDataModule(organizationId, token);
      return cubeDataModule.getCycleTimeData(
        dateRange,
        dateGranularity,
        filters
      );
    },

    async getCustomCycleTimeData(
      dateRange: DateTimeRange,
      dateGranularity: 'day' | 'week' | 'month',
      fromLaneId: string,
      toLaneId: string,
      filters?: StoredQueryOptions
    ) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      const token = await this.getCubeAuth(organizationId);
      const cubeDataModule = new CubeDataModule(organizationId, token);
      return cubeDataModule.getCustomCycleTimeData(
        dateRange,
        dateGranularity,
        fromLaneId,
        toLaneId,
        filters
      );
    },

    async getCustomCycleTimeSumsData(
      dateRange: DateTimeRange,
      fromLaneId: string,
      toLaneId: string,
      filters?: StoredQueryOptions
    ) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      const token = await this.getCubeAuth(organizationId);
      const cubeDataModule = new CubeDataModule(organizationId, token);
      return cubeDataModule.getCustomCycleTimeSumsData(
        dateRange,
        fromLaneId,
        toLaneId,
        filters
      );
    },
  },
});
